import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, Trash2, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { useStore, FREE_SHIP } from "../lib/store";
import { API, money } from "../lib/api";

export default function Cart() {
  const { lines, subtotal, updateQty, removeLine } = useStore();
  const [busy, setBusy] = useState(false);
  const left = Math.max(0, FREE_SHIP - subtotal);
  const pct = Math.min(100, (subtotal / FREE_SHIP) * 100);

  const checkout = async () => {
    if (!API) return toast.error("Checkout is unavailable right now");
    setBusy(true);
    try {
      const items = lines.map((l) => ({ product_id: l.product_id, quantity: l.quantity, size: l.size, color: l.color }));
      const res = await axios.post(`${API}/payments/checkout`, { items, origin_url: window.location.origin });
      if (res.data?.checkout_url) window.location.href = res.data.checkout_url;
      else throw new Error("no url");
    } catch (e) {
      toast.error(e.response?.data?.detail || "We couldn't start checkout. Please try again.");
      setBusy(false);
    }
  };

  if (!lines.length) {
    return (
      <main data-testid="cart-page" className="mx-auto max-w-3xl px-5 py-32 text-center">
        <p className="font-script text-6xl sm:text-7xl text-plum">Your bag is empty</p>
        <p className="mt-4 text-plum/75">Little luxuries are waiting to be discovered.</p>
        <Link to="/shop" data-testid="cart-empty-shop-button" className="btn-luxe mt-8">Start shopping</Link>
      </main>
    );
  }

  return (
    <main data-testid="cart-page" className="mx-auto max-w-7xl px-4 sm:px-8 pt-12 pb-28">
      <h1 className="font-display text-5xl sm:text-6xl italic">Your Bag</h1>
      <div className="mt-10 grid gap-12 lg:grid-cols-[1fr_380px]">
        <ul className="divide-y divide-plum/10 border-y border-plum/10">
          <AnimatePresence initial={false}>
            {lines.map((l) => (
              <motion.li key={l.key} layout exit={{ opacity: 0, x: -40 }} data-testid={`cart-line-${l.product_id}`} className="flex gap-4 sm:gap-6 py-6">
                <Link to={`/product/${l.product_id}`} className="h-32 w-24 sm:h-40 sm:w-32 shrink-0 overflow-hidden rounded-2xl rounded-tl-[2.5rem]">
                  <img src={l.product.image} alt={l.product.name} className="h-full w-full object-cover" />
                </Link>
                <div className="flex flex-1 flex-col justify-between">
                  <div className="flex justify-between gap-4">
                    <div>
                      <p className="eyebrow text-[10px] text-plum/60">{l.product.category}</p>
                      <p className="mt-1 font-display text-lg sm:text-xl">{l.product.name}</p>
                      <p className="mt-1 flex items-center gap-2 text-xs text-plum/70">
                        {l.color && <span className="inline-block h-3.5 w-3.5 rounded-full border border-plum/20" style={{ background: l.color }} />}
                        {l.size && <span>Size {l.size}</span>}
                      </p>
                    </div>
                    <p className="font-semibold">{money(l.product.price * l.quantity)}</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center rounded-full border border-plum/20">
                      <button aria-label="Decrease" data-testid={`cart-line-${l.product_id}-decrease`} onClick={() => updateQty(l.key, l.quantity - 1)} className="grid h-9 w-9 place-items-center"><Minus className="h-3.5 w-3.5" /></button>
                      <span data-testid={`cart-line-${l.product_id}-qty`} className="w-6 text-center text-sm font-semibold">{l.quantity}</span>
                      <button aria-label="Increase" data-testid={`cart-line-${l.product_id}-increase`} onClick={() => updateQty(l.key, l.quantity + 1)} className="grid h-9 w-9 place-items-center"><Plus className="h-3.5 w-3.5" /></button>
                    </div>
                    <button data-testid={`cart-line-${l.product_id}-remove`} onClick={() => removeLine(l.key)} className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-plum/70 hover:text-magenta"><Trash2 className="h-4 w-4" /> Remove</button>
                  </div>
                </div>
              </motion.li>
            ))}
          </AnimatePresence>
        </ul>

        <aside className="h-fit rounded-[2rem] bg-white p-7 shadow-[0_20px_60px_-30px_rgba(42,15,46,.35)] lg:sticky lg:top-44">
          <p className="font-display text-2xl italic">Order summary</p>
          <div className="mt-6">
            <p data-testid="cart-free-shipping-message" className="text-sm text-plum/80">
              {left > 0 ? <>You're <b className="text-magenta">{money(left)}</b> away from free shipping</> : <b className="text-teal">You've unlocked free shipping!</b>}
            </p>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-cream-warm">
              <motion.div className="h-full rounded-full bg-gradient-to-r from-magenta via-tangerine to-gold" initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8 }} />
            </div>
          </div>
          <div className="mt-6 space-y-3 border-t border-plum/10 pt-6 text-sm">
            <div className="flex justify-between"><span className="text-plum/75">Subtotal</span><span data-testid="cart-subtotal" className="font-semibold">{money(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-plum/75">Shipping</span><span data-testid="cart-shipping-estimate" className="text-plum/75">{left > 0 ? "$5.95 at checkout" : "Free"}</span></div>
            <div className="flex justify-between"><span className="text-plum/75">Tax</span><span className="text-plum/75">Calculated at checkout</span></div>
          </div>
          <button data-testid="cart-checkout-button" onClick={checkout} disabled={busy} className="btn-luxe mt-7 w-full disabled:opacity-70">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Secure Checkout
          </button>
          <p className="mt-4 text-center text-xs text-plum/65">Test mode: use card 4242 4242 4242 4242, any future date, any CVC.</p>
          <Link to="/shop" data-testid="cart-continue-shopping" className="mt-4 block text-center text-xs font-semibold uppercase tracking-[0.2em] text-magenta hover:underline">Continue shopping</Link>
        </aside>
      </div>
    </main>
  );
}
